import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { LoqateValidationFields } from '@/types/Loqate';
import { WizardValidationError } from '@/types/Wizard';
import { RapidStorSettings } from '@/utils/rapidstor';
import { getAutobillFields } from '@/utils/validation';

import CircleCheckbox from '@/components/elements/circle-checkbox';
import ErrorTag from '@/components/elements/error-tag';

import classes from './styles.module.scss';

import { CardExpiry } from '../payment-form';

interface Props {
    form: Record<string, string>;
    update: (arg: Record<string, string> | DraftFunction<Record<string, string>>) => void;
    loqateValidation: (field: string) => void;
    updateExpiryDate: (
        values: Record<string, string | number>,
        autobill?: boolean
    ) => void;
    loqateLoading?: LoqateValidationFields;
    validationErrors?: WizardValidationError[];
    bankName: string | undefined;
}

export default function AutoBill({
    form,
    update,
    loqateValidation,
    updateExpiryDate,
    loqateLoading,
    validationErrors,
    bankName,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();

    const formErrors = useMemo(() => {
        const inputNames = getAutobillFields(
            location,
            form.autobillType === 'cc',
            form.autobillUsePaymentDetails === 'true'
        );

        return validationErrors?.filter((error) => inputNames.includes(error.name));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationErrors]);

    return (
        <div className={`${classes.container}`}>
            <span className={`${classes.header_text}`}>
                {t('wizard:autobillDetails:title')}
            </span>

            <span
                className={`${classes.subtitle}`}
                dangerouslySetInnerHTML={{
                    __html: location.rapidstor.wizards.moveinSingle.autobillContent,
                }}
            ></span>

            <div className={`${classes.autobill_type}`}>
                {location.rapidstor.directDebit?.enabled && (
                    <CircleCheckbox
                        label={t('wizard:autobillDetails:directDebit')}
                        value={form.autobillType === 'dd' ? 'true' : 'false'}
                        update={() => {
                            update((draft) => {
                                draft.autobillType = 'dd';
                            });
                        }}
                        formKey='autobill_dd'
                    />
                )}

                {location.rapidstor.creditCardForAutobill.enabled && (
                    <CircleCheckbox
                        label={t('wizard:autobillDetails:creditCard')}
                        value={form.autobillType === 'cc' ? 'true' : 'false'}
                        update={() => {
                            update((draft) => {
                                draft.autobillType = 'cc';
                                if (location.rapidstor.forceInitialCard) {
                                    draft.autobillUsePaymentDetails = 'true';
                                }
                            });
                        }}
                        formKey='autobill_cc'
                    />
                )}

                {!RapidStorSettings(location).autobill().required() && (
                    <CircleCheckbox
                        label={t(
                            'wizard:autobillDetails:none'
                        )}
                        value={form.autobillType === '' ? 'true' : 'false'}
                        update={() => {
                            update((draft) => {
                                draft.autobillType = '';
                            });
                        }}
                        formKey='autobill_none'
                    />
                )}
            </div>

            {formErrors?.map((error) => (
                <ErrorTag key={`${error.name}_error_idx`} error={error} />
            ))}

            {bankName && <span className={`${classes.bankName}`}>{bankName}</span>}

            {form.autobillType === 'dd' && (
                <div className={`${classes.directDebitDetails}`}>
                    <input
                        type='text'
                        onChange={(e) => {
                            if (e.currentTarget.value.length <= 6) {
                                update((draft) => {
                                    draft.autobillBsbNumber = e.target.value;
                                });
                            }
                        }}
                        value={form.autobillBsbNumber}
                        disabled={loqateLoading === 'bank'}
                        className={
                            formErrors?.some((error) => error.name === 'bank')
                                ? classes.invalid
                                : ''
                        }
                        onBlur={() => loqateValidation('bank')}
                        placeholder={t('wizard:autobillDetails:bsbNumber')}
                    />
                    <input
                        type='text'
                        onChange={(e) => {
                            if (e.currentTarget.value.length <= 9) {
                                update((draft) => {
                                    draft.autobillAccountNumber = e.target.value;
                                });
                            }
                        }}
                        value={form.autobillAccountNumber}
                        className={
                            formErrors?.some((error) => error.name === 'bank')
                                ? classes.invalid
                                : ''
                        }
                        disabled={loqateLoading === 'bank'}
                        onBlur={() => loqateValidation('bank')}
                        placeholder={t('wizard:autobillDetails:accountNumber')}
                    />
                </div>
            )}

            {form.autobillType === 'cc' && (
                <>
                    {!location.rapidstor.tenantAccounts.autoPayment.enableSwitchingToDebit && 
                    t('wizard:autobillDetails:switchToDebit') && (
                        <p>{t('wizard:autobillDetails:switchToDebit')}</p>
                    )}
                    {!location.rapidstor.forceInitialCard && (
                        <CircleCheckbox
                            label='Use Payment Details'
                            value={form.autobillUsePaymentDetails}
                            formKey='usePaymentDetails'
                            update={() => {
                                update((draft) => {
                                    draft.autobillUsePaymentDetails =
                                        draft.autobillUsePaymentDetails === 'true'
                                            ? 'false'
                                            : 'true';
                                });
                            }}
                        />
                    )}
                    {form.autobillUsePaymentDetails === 'false' && (
                        <div className={`${classes.inputs}`}>
                            <div>
                                <input
                                    type='text'
                                    placeholder='Card Holder Name*'
                                    required
                                    value={form.autobillCreditCardName}
                                    onChange={(e) => {
                                        update((draft) => {
                                            draft.autobillCreditCardName = e.target.value;
                                        });
                                    }}
                                    name='cardHolderName'
                                />
                            </div>

                            <div>
                                <input
                                    type='text'
                                    placeholder='Card Number*'
                                    required
                                    value={form.autobillCreditCardNumber}
                                    onChange={(e) => {
                                        update((draft) => {
                                            draft.autobillCreditCardNumber =
                                                e.target.value;
                                        });
                                    }}
                                    name='cardNumber'
                                />
                            </div>

                            <div>
                                <CardExpiry
                                    required
                                    name='cardExpiry'
                                    onValueChange={(
                                        values: Record<string, string | number>
                                    ) => {
                                        updateExpiryDate(values, true);
                                    }}
                                    month={form.autobillCreditCardExpiryMonth}
                                    year={form.autobillCreditCardExpiryYear}
                                />
                            </div>

                            <div>
                                <input
                                    type='text'
                                    placeholder='CSV*'
                                    name='cardCsv'
                                    value={form.autobillCreditCardCsv}
                                    onChange={(e) => {
                                        update((draft) => {
                                            draft.autobillCreditCardCsv = e.target.value;
                                        });
                                    }}
                                    required
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
